/* eslint-disable jsx-a11y/anchor-is-valid */
import React, {ReactNode, useEffect, useState} from 'react'
import {
  IPOInfoFilter,
  IPOInfoFilterInitialState,
  IPOInfoModel,
  IPOTypes,
  IRemovedPOItem,
} from './core/_models'
import NormalMessage from '../common/NormalMessage'
import Loader from '../common/Loader'
import {useParams, useNavigate} from 'react-router-dom'
import PurchaseTable from './components/purchase/PurchaseTable'
import RemovedPurchaseTable from './components/purchase/RemovedPurchaseTable'
import {getPurchaseOrderInfo} from './core/_requests'
import SharedData from '../common/SharedData'
import { useIntl } from 'react-intl'
import {brandLogo} from '../../tenant/applyBranding'
import {isDefaultTenant, tenantBrandName} from '../../tenant/tenantState'

const PurchaseOrder: React.FC = () => {
  const {formatMessage} = useIntl();
  const {orderID} = useParams()
  const navigate = useNavigate()

  const {currentUser,language} = SharedData();
  const [purchaseList, setPurchaseList] = useState<IPOInfoModel[]>([])
  const [removedList, setRemovedList] = useState<IRemovedPOItem[]>([])
  const [resultFilters, setResultFilters] = useState<IPOInfoFilter>({
    ...IPOInfoFilterInitialState,
    type: IPOTypes.po,
    supplier_order_id: orderID || ''
  })

  const [isLoading, setIsLoading] = useState(false)
  const [destination, setDestination] = useState<string>('')
  

  useEffect(() => {
    getData()
  }, [])

  const getData = async () => {
    setIsLoading(true)
    const {data} = await getPurchaseOrderInfo(resultFilters, currentUser?.token, language)
    setDestination(data?.destination ?? '')
    if (data) {
      // prefer po_items (indexed array), fallback to po_info (object)
      if (data.po_items && Array.isArray(data.po_items)) {
        setPurchaseList(data.po_items)
      } else if (data.po_info) {
        const responseData = data.po_info;
        const products = []
        for (let key in responseData) {
          products.push(responseData[key])
        }
        setPurchaseList(products)
      } else {
        setPurchaseList([])
      }

      if (data.removed_po_items && Array.isArray(data.removed_po_items)) {
        setRemovedList(data.removed_po_items)
      } else {
        setRemovedList([])
      }
    }
    setIsLoading(false)
  }

  // Export the PO line items to a PDF, client-side from the already-loaded data
  // — no extra API call. We build a styled HTML table and hand it to the browser's
  // print pipeline (Save as PDF), which renders Arabic/RTL natively without any
  // extra fonts or dependencies.
  const handleExport = () => {
    if (purchaseList.length === 0) return
    const isRTL = language === 'ar'
    const esc = (v: unknown) =>
      String(v ?? '')
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;')
    const money = (n: number) => (Number(n) || 0).toFixed(2)
    const sum = (f: (i: IPOInfoModel) => number) => purchaseList.reduce((s, i) => s + (f(i) || 0), 0)

    const cols = [
      {label: formatMessage({id: 'barcode'}), num: false},
      {label: formatMessage({id: 'model_num'}), num: false},
      {label: formatMessage({id: 'prod_name'}), num: false},
      {label: formatMessage({id: 'options', defaultMessage: 'Options'}), num: false},
      {label: formatMessage({id: 'quantity'}), num: true},
      {label: formatMessage({id: 'received_qty'}), num: true},
      {label: formatMessage({id: 'cost_price'}), num: true},
      {label: formatMessage({id: 'sale_price'}), num: true},
      {label: formatMessage({id: 'total_cost'}), num: true},
      {label: formatMessage({id: 'status'}), num: false},
    ]
    const headerCells = cols.map((c) => `<th class="${c.num ? 'num' : ''}">${esc(c.label)}</th>`).join('')

    const bodyRows = purchaseList
      .map((item) => {
        const options = (item.options || []).map((o) => `${o.name}: ${o.value}`).join(', ')
        return (
          '<tr>' +
          `<td>${esc(item.barcode)}</td>` +
          `<td>${esc(item.model)}</td>` +
          `<td>${esc(item.name)}</td>` +
          `<td>${esc(options)}</td>` +
          `<td class="num accent">${Number(item.quantity) || 0}</td>` +
          `<td class="num">${Number(item.received_quantity) || 0}</td>` +
          `<td class="num">${money(item.cost)}</td>` +
          `<td class="num">${money(item.price)}</td>` +
          `<td class="num">${money(item.total_cost)}</td>` +
          `<td>${esc(item.status)}</td>` +
          '</tr>'
        )
      })
      .join('')

    const totalRow =
      '<tr class="total-row">' +
      '<td></td><td></td><td></td>' +
      `<td>${esc(formatMessage({id: 'total', defaultMessage: 'TOTAL'}))}</td>` +
      `<td class="num accent">${sum((i) => Number(i.quantity))}</td>` +
      `<td class="num accent">${sum((i) => Number(i.received_quantity))}</td>` +
      '<td></td><td></td>' +
      `<td class="num accent">${money(sum((i) => Number(i.total_cost)))}</td>` +
      '<td></td>' +
      '</tr>'

    // Brand header mirrors the manager-666 "Save PDF" invoice: centered tenant
    // logo, a navy #283592 rule + title, #E7EFEF table header, #CDDDDD borders,
    // and #1e90ff accents on quantities/totals. Dumyah's invoice logo is only a
    // fallback for Dumyah; another tenant without a logo gets its name instead.
    const logoUrl = brandLogo(isRTL, isDefaultTenant() ? 'https://www.dumyah.com/image/data/asset/invoice-logo.png' : '')
    const brandName = tenantBrandName(isRTL ? 'ar' : 'en')
    const poNumber = `${formatMessage({id: 'po_num'})} ${orderID ?? ''}`
    const title = formatMessage({id: 'purchase_order', defaultMessage: 'Purchase Order'})
    const html =
      '<!DOCTYPE html>' +
      `<html dir="${isRTL ? 'rtl' : 'ltr'}" lang="${isRTL ? 'ar' : 'en'}">` +
      '<head><meta charset="utf-8" />' +
      `<title>${esc(`purchase-order-${orderID}`)}</title>` +
      '<style>' +
      '@page { size: A4 landscape; margin: 12mm; }' +
      '* { box-sizing: border-box; }' +
      'body { font-family: Verdana, Arial, Helvetica, sans-serif; color: #111; margin: 0; font-size: 12px; -webkit-print-color-adjust: exact; print-color-adjust: exact; }' +
      '.brand { text-align: center; margin: 0 0 4px; }' +
      '.brand img { width: 200px; max-width: 60%; height: auto; }' +
      'hr.rule { border: none; border-top: 6px solid #283592; margin: 8px 0 10px; }' +
      'h2.title { color: #283592; font-size: 28px; margin: 0 0 6px; }' +
      '.po-number { color: #555353; font-weight: bold; margin: 0 0 14px; }' +
      'table { width: 100%; border-collapse: collapse; font-size: 11px; }' +
      'thead { display: table-header-group; }' +
      `th, td { border: 1px solid #CDDDDD; padding: 5px; text-align: ${isRTL ? 'right' : 'left'}; vertical-align: top; }` +
      'th { background: #E7EFEF; font-weight: 700; }' +
      'td.num, th.num { text-align: right; white-space: nowrap; }' +
      'td.accent { color: #1e90ff; font-weight: 700; font-size: larger; }' +
      'tr { page-break-inside: avoid; }' +
      'tr.total-row td { font-weight: 700; background: #f4f6fb; }' +
      '</style></head>' +
      '<body>' +
      (logoUrl
        ? `<div class="brand"><img src="${esc(logoUrl)}" alt="${esc(brandName)}" /></div>`
        : `<div class="brand"><h1>${esc(brandName)}</h1></div>`) +
      '<hr class="rule" />' +
      `<h2 class="title">${esc(title)}</h2>` +
      `<div class="po-number">${esc(poNumber)}</div>` +
      `<table><thead><tr>${headerCells}</tr></thead>` +
      `<tbody>${bodyRows}${totalRow}</tbody></table></body></html>`

    // Print via a hidden iframe (no popup-blocker issues, no stray tab). The user
    // picks "Save as PDF" as the print destination.
    const iframe = document.createElement('iframe')
    iframe.setAttribute('aria-hidden', 'true')
    iframe.style.cssText = 'position:fixed;right:0;bottom:0;width:0;height:0;border:0;'
    iframe.srcdoc = html
    const cleanup = () => {
      if (iframe.parentNode) iframe.parentNode.removeChild(iframe)
    }
    iframe.onload = () => {
      const win = iframe.contentWindow
      if (!win) {
        cleanup()
        return
      }
      const doPrint = () => {
        win.onafterprint = cleanup
        win.focus()
        win.print()
        // Safety net in case onafterprint never fires (harmless hidden iframe).
        setTimeout(cleanup, 60000)
      }
      // Gate on the brand logo so the dialog doesn't open before it paints;
      // fall back after 2s if the external image is slow or blocked.
      const logo = win.document.querySelector('img')
      if (logo && !logo.complete) {
        let done = false
        const once = () => {
          if (done) return
          done = true
          doPrint()
        }
        logo.onload = once
        logo.onerror = once
        setTimeout(once, 2000)
      } else {
        doPrint()
      }
    }
    document.body.appendChild(iframe)
  }

  const resultsData = (): ReactNode => {
    if (isLoading) {
      return <Loader />
    }
    
    // Show tables if either purchaseList or removedList has items
    if (purchaseList.length > 0 || removedList.length > 0) {
      return (
        <>
          {purchaseList.length > 0 && (
            <div className='card-body py-3'>
              <PurchaseTable orders={purchaseList} refreshData={getData} />
            </div>
          )}
          {removedList.length > 0 && (
            <div className='card-body py-3 pt-0'>
              {purchaseList.length > 0 && (
                <div className='separator border-bottom border-gray-300 my-5'></div>
              )}
              <div className='d-flex justify-content-between align-items-center mb-2' style={{padding: '20px'}}>
                <h5 className='fw-bold mb-0'>{formatMessage({ id: "removed_po_products" , defaultMessage: "Removed PO Products" })}</h5>
                <span className='badge badge-light-primary'> {formatMessage({ id: "num_of_removed_po_products" , defaultMessage: "Number of Removed PO Products" })} : {removedList.length}</span> 
              </div>
              <RemovedPurchaseTable items={removedList} />
            </div>
          )}
        </>
      )
    }
    
    return <NormalMessage msg={formatMessage({ id: "no_data_po" })} />
  }

  return (
    <div className={`card`}>
      {/* begin::Header */}
      <div className='card-header border-0 pt-5 d-flex justify-content-between align-items-center'>
        <h3 className='card-title align-items-start flex-column mb-0'>
          <span className='card-label fw-bold fs-3'>{formatMessage({ id: "po_num" })} {orderID}</span>
          {/* WHICH STORE this PO delivers to — the same line the backoffice PO screen shows. */}
          {destination ? (
            <span className='text-muted fw-semibold fs-7 mt-1'>
              {formatMessage({ id: 'po_destination', defaultMessage: 'Destination' })}: {destination}
            </span>
          ) : null}
        </h3>

        <div className='d-flex align-items-center gap-2'>
          <button
            className='btn btn-light-primary d-flex align-items-center gap-2'
            onClick={handleExport}
            disabled={purchaseList.length === 0}
          >
            <i className='fas fa-file-pdf'></i>
            {formatMessage({ id: "export", defaultMessage: "Export" })}
          </button>

          <button
            className='btn btn-primary'
            onClick={() => navigate(`/orders/purchase-order/${orderID}/invoice`)}
          >
            {formatMessage({ id: "invoice", defaultMessage: "Invoice" })}
          </button>
        </div>
      </div>
      {/* end::Header */}

      {resultsData()}
    </div>
  )
}

export {PurchaseOrder}
